import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeftFromLine,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Printer,
  RotateCcw,
  UserCheck,
  XCircle,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useToast } from "#/components/common/ToastProvider";
import { leaveApi } from "../api";
import { AttachmentPreview } from "../components/AttachmentPreview";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeaveStatusBadge } from "../components/LeaveStatusBadge";
import { Btn } from "../components/LeaveUI";
import { getErrorMessage, useAsyncData, useVoidMutation } from "../hooks";
import { exportToPdf, exportToExcel } from "../lib/exportUtils";
import type { LeaveApplicationDetails } from "../types";

export default function LeaveRequestViewPage() {
  const params   = useParams();
  const id       = Number(params.id);
  const navigate = useNavigate();

  const { hasPermission, hasRole, currentUser } = useAuth();
  const canManage = hasRole("SUPER_ADMIN") || hasPermission("HR_LEAVE_MANAGE");
  const toast = useToast();

  const currentEmployee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const [comments, setComments] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // ── Recall / early-return form state ──
  const [recallDate, setRecallDate] = useState("");
  const [recallReason, setRecallReason] = useState("");
  const [earlyReturnDate, setEarlyReturnDate] = useState("");
  const [earlyReturnReason, setEarlyReturnReason] = useState("");

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.getApplication(id),
    [id],
  );

  const approve     = useVoidMutation(() => leaveApi.approve(id, { comments }));
  const reject      = useVoidMutation(() => leaveApi.reject(id, { comments }));
  const cancel      = useVoidMutation(() => leaveApi.cancel(id, comments));
  const recall      = useVoidMutation(() =>
    leaveApi.recall(id, { actualReturnDate: recallDate, reason: recallReason || null }),
  );
  const earlyReturn = useVoidMutation(() =>
    leaveApi.earlyReturn(id, { actualReturnDate: earlyReturnDate, reason: earlyReturnReason || null }),
  );

  async function act(action: () => Promise<unknown>, successMessage?: string) {
    try {
      await action();
      setComments("");
      await reload();
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleExportPdf() {
    if (!data) return;
    setExporting(true);
    try {
      await exportToPdf({
        title: `Leave Application — ${data.applicationNo}`,
        subtitle: `${data.employeeName} | ${data.leaveTypeName} | Generated: ${new Date().toLocaleString()}`,
        headers: ["Field", "Value"],
        rows: buildDetailRows(data),
        filename: data.applicationNo,
        colWidths: [0.3, 0.7],
      });
    } finally { setExporting(false); }
  }

  async function handleExportExcel() {
    if (!data) return;
    setExportingExcel(true);
    try {
      await exportToExcel("Leave Application", ["Field", "Value"], buildDetailRows(data), data.applicationNo);
    } finally { setExportingExcel(false); }
  }

  if (!id || Number.isNaN(id)) return <LeaveError message="Invalid leave request ID." />;
  if (loading)  return <LeaveLoading label="Loading leave request…" />;
  if (error)    return <LeaveError message={error} onRetry={reload} />;
  if (!data)    return <LeaveError message="Leave request not found." />;

  const isDraft    = data.status === "DRAFT";
  const isPending  = data.status === "PENDING_APPROVAL";
  const isApproved = data.status === "APPROVED";
  const isRecalled = data.status === "RECALLED";
  const isTerminal = ["APPROVED", "REJECTED", "CANCELLED", "RECALLED"].includes(data.status);
  const isOwnApplication = currentEmployee.data?.id === data.employeeId;
  const isOwnRelief = currentEmployee.data && data.reliefEmployeeId === currentEmployee.data.id;

  // Validate recall date constraints (must be within [startDate, endDate])
  const recallDateValid =
    recallDate >= data.startDate && recallDate <= data.endDate;
  const earlyReturnDateValid =
    earlyReturnDate >= data.startDate && earlyReturnDate <= data.endDate;

  const isAssignedApprover =
    data.currentApproverUsername != null
      ? currentUser?.username === data.currentApproverUsername
      : false;
  const canAct = canManage || isAssignedApprover;
  const notYourStepMessage = data.currentApproverUsername
    ? `The next approver is ${formatUsername(data.currentApproverUsername)}`
    : "You are not assigned to approve this application";

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <LeavePageHeader
        breadcrumb="Leave Management · My Requests"
        title={data.applicationNo}
        description={`${data.employeeName} · ${data.leaveTypeName} · ${data.requestedDays} day(s)`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <LeaveBackButton to="/hr/leave/requests" label="My Requests" />
            <Btn variant="secondary" size="md" icon={FileText} loading={exporting} onClick={handleExportPdf}>
              PDF
            </Btn>
            <Btn variant="secondary" size="md" icon={Download} loading={exportingExcel} onClick={handleExportExcel}>
              Excel
            </Btn>
            <Btn variant="secondary" size="icon" onClick={() => window.print()} title="Print">
              <Printer className="h-4 w-4" />
            </Btn>
          </div>
        }
      />

      {/* ── Relief employee banner ── */}
      {isOwnRelief && (isApproved || isPending) && (
        <div className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4">
          <UserCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
          <div>
            <p className="font-semibold text-blue-900">
              You are the relief for <strong>{data.employeeName}</strong> until{" "}
              <strong>{formatDate(data.endDate)}</strong>.
            </p>
            <p className="mt-0.5 text-sm text-blue-700">
              {isPending ? "Still pending approval." : "Approved."}
            </p>
          </div>
        </div>
      )}

      {/* ── Draft banner ── */}
      {isDraft && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-500" />
            <div>
              <p className="font-semibold text-amber-900">Draft — not yet submitted</p>
              <p className="text-sm text-amber-700">Submit when ready to send for approval.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(`/hr/leave/requests/${id}/edit`)}
              className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700"
            >
              Edit &amp; Submit
            </button>
            <button
              type="button"
              disabled={cancel.loading}
              onClick={() => act(cancel.mutate, "Leave application cancelled.")}
              className="rounded-xl border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100 disabled:opacity-50"
            >
              {cancel.loading ? "Cancelling…" : "Discard"}
            </button>
          </div>
        </div>
      )}

      {/* ── Approval progress stepper ── */}
      {!isTerminal && !isDraft && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Approval Progress</p>
          </div>
          <div className="px-5 py-5">
            <div className="flex items-center gap-0">
              {(["Submitted", "Under Review", "Approved"] as const).map((label, i) => {
                const reached  = i === 0 || (i === 1 && data.status !== "DRAFT") || (i === 2 && isApproved);
                const isCurrent = (i === 1 && isPending) || (i === 2 && isApproved);
                return (
                  <div key={label} className="flex flex-1 items-center">
                    {i > 0 && (
                      <div className={`h-0.5 flex-1 transition-colors ${reached ? "bg-emerald-400" : "bg-slate-200"}`} />
                    )}
                    <div className="flex flex-col items-center gap-1.5">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ring-4 transition-all ${
                        isCurrent
                          ? "bg-emerald-600 text-white ring-emerald-100"
                          : reached
                            ? "bg-emerald-100 text-emerald-700 ring-transparent"
                            : "bg-slate-100 text-slate-400 ring-transparent"
                      }`}>
                        {i + 1}
                      </div>
                      <span className={`text-xs font-semibold ${isCurrent ? "text-emerald-700" : "text-slate-400"}`}>
                        {label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {data.currentStepName && (
              <p className="mt-4 text-center text-xs text-slate-400">
                Current step: <span className="font-semibold text-slate-600">{data.currentStepName}</span>
                {data.currentApproverUsername && (
                  <> · Awaiting <span className="font-semibold text-slate-600">{data.currentApproverUsername}</span></>
                )}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Summary stat cards ── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Status"          value={<LeaveStatusBadge value={data.status} />} />
        <StatCard
          label={isRecalled && data.actualDaysUsed != null ? "Days Used / Requested" : "Days Requested"}
          value={
            isRecalled && data.actualDaysUsed != null ? (
              <span>
                <span className="text-2xl font-bold text-slate-900">{data.actualDaysUsed}</span>
                <span className="text-sm text-slate-400"> / {data.requestedDays}</span>
              </span>
            ) : (
              <span className="text-2xl font-bold text-slate-900">{data.requestedDays}</span>
            )
          }
        />
        <StatCard label="Workflow Step"    value={data.currentStepName || "—"} />
        <StatCard label="Current Approver" value={data.currentApproverUsername || "—"} />
      </div>

      {/* ── Action errors ── */}
      {[approve.error, reject.error, cancel.error, recall.error, earlyReturn.error].filter(Boolean).map((msg, i) => (
        <LeaveError key={i} message={String(msg)} />
      ))}

      {/* ── Leave details ── */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
          <h2 className="font-semibold text-slate-900">Leave Details</h2>
          {isPending && isOwnApplication && (
            <Btn
              variant="danger"
              size="md"
              icon={XCircle}
              disabled={cancel.loading}
              onClick={() => act(cancel.mutate, "Leave application cancelled.")}
            >
              {cancel.loading ? "Withdrawing…" : "Withdraw Application"}
            </Btn>
          )}
        </div>
        <div className="grid gap-x-8 gap-y-5 p-5 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Detail label="Application No."  value={<span className="font-mono text-xs font-semibold">{data.applicationNo}</span>} />
          <Detail label="Employee No."     value={data.employeeNumber} />
          <Detail label="Employee"         value={data.employeeName} />
          <Detail label="Department"       value={data.departmentName || "—"} />
          <Detail label="Job Title"        value={data.jobTitle || "—"} />
          <Detail label="Leave Type"       value={data.leaveTypeName} />
          <Detail label="Start Date"       value={formatDate(data.startDate)} />
          <Detail label="End Date"         value={formatDate(data.endDate)} />
          <Detail label="Days"             value={`${data.requestedDays} day(s)`} />
          <Detail label="Relief Employee"  value={data.reliefEmployeeName || "—"} />
          <Detail label="Workflow"         value={data.workflowCode || "—"} />
          <Detail label="Submitted"        value={formatDateTime(data.submittedAt)} />
          {data.approvedAt  && <Detail label="Approved At"  value={formatDateTime(data.approvedAt)} />}
          {data.rejectedAt  && <Detail label="Rejected At"  value={formatDateTime(data.rejectedAt)} />}
          {data.cancelledAt && <Detail label="Cancelled At" value={formatDateTime(data.cancelledAt)} />}
          {isRecalled && data.recalledAt && (
            <Detail label="Recalled At" value={formatDateTime(data.recalledAt)} />
          )}
          {isRecalled && data.actualReturnDate && (
            <Detail label="Actual Return Date" value={formatDate(data.actualReturnDate)} />
          )}
          {isRecalled && data.actualDaysUsed != null && (
            <Detail
              label="Actual Days Used"
              value={
                <span>
                  <span className="font-bold">{data.actualDaysUsed}</span>
                  {data.requestedDays !== data.actualDaysUsed && (
                    <span className="ml-1.5 text-xs text-slate-400">
                      of {data.requestedDays} requested
                      {(data.requestedDays - data.actualDaysUsed) > 0 && (
                        <> · <span className="font-semibold text-emerald-600">
                          {data.requestedDays - data.actualDaysUsed}d credited back
                        </span></>
                      )}
                    </span>
                  )}
                </span>
              }
            />
          )}
          {data.reason && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Detail label="Reason" value={data.reason} />
            </div>
          )}
          {data.remarks && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Detail label="Remarks" value={data.remarks} />
            </div>
          )}
          {isRecalled && data.recallReason && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Detail label="Recall Reason" value={data.recallReason} />
            </div>
          )}
          {(data.attachmentDownloadUrl || data.attachmentPath) && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Detail
                label="Attachment"
                value={
                  data.attachmentDownloadUrl
                    ? <AttachmentPreview url={data.attachmentDownloadUrl} label="View Supporting Document" />
                    : <span className="text-xs text-slate-500">{data.attachmentPath}</span>
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Take Action panel ── */}
      {isPending && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-5 py-4">
            <h2 className="font-semibold text-slate-900">Take Action</h2>
            <p className="mt-0.5 text-sm text-slate-400">
              {canAct
                ? "Add an optional comment, then approve or reject."
                : notYourStepMessage + "."}
            </p>
          </div>
          <div className="p-5">
            {!canAct && (
              <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                {notYourStepMessage}.
              </div>
            )}
            <textarea
              disabled={!canAct}
              className="min-h-[96px] w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50 disabled:text-slate-400"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Optional comments…"
            />
            <div className="mt-4 flex flex-wrap gap-2">
              <Btn
                variant="primary"
                size="md"
                icon={CheckCircle2}
                disabled={!canAct}
                loading={approve.loading}
                loadingText="Approving…"
                title={!canAct ? notYourStepMessage : undefined}
                onClick={() => canAct && act(approve.mutate, "Leave application approved.")}
              >
                Approve
              </Btn>
              <Btn
                variant="dangerOutline"
                size="md"
                icon={XCircle}
                disabled={!canAct}
                loading={reject.loading}
                loadingText="Rejecting…"
                title={!canAct ? notYourStepMessage : undefined}
                onClick={() => canAct && act(reject.mutate, "Leave application rejected.")}
              >
                Reject
              </Btn>
              {canManage && !isOwnApplication && (
                <Btn
                  variant="ghost"
                  size="md"
                  icon={AlertTriangle}
                  disabled={cancel.loading}
                  onClick={() => act(cancel.mutate, "Leave application cancelled.")}
                >
                  {cancel.loading ? "Cancelling…" : "Cancel Request"}
                </Btn>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── HR Recall panel ── */}
      {isApproved && canManage && (
        <div className="overflow-hidden rounded-2xl border border-orange-200 bg-white shadow-sm">
          <div className="border-b border-orange-100 bg-orange-50 px-5 py-4">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-orange-600" />
              <h2 className="font-semibold text-orange-900">Recall from Leave</h2>
            </div>
            <p className="mt-0.5 text-sm text-orange-700">
              Terminate the leave early and credit unused days back to the employee's balance.
            </p>
          </div>
          <div className="space-y-4 p-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Actual Return Date <span className="text-red-500">*</span>
              </span>
              <input
                type="date"
                className="input"
                value={recallDate}
                min={data.startDate}
                max={data.endDate}
                onChange={(e) => setRecallDate(e.target.value)}
              />
              {recallDate && !recallDateValid && (
                <p className="text-xs font-semibold text-red-600">
                  Must be within the approved leave period ({formatDate(data.startDate)} – {formatDate(data.endDate)}).
                </p>
              )}
              {!recallDate && (
                <p className="text-xs text-slate-400">
                  The date the employee actually returned (days before this date will be counted as used; the rest credited back).
                </p>
              )}
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">Reason</span>
              <textarea
                className="input min-h-[80px] resize-y"
                value={recallReason}
                onChange={(e) => setRecallReason(e.target.value)}
                placeholder="e.g. Operational requirements, staff shortage…"
              />
            </label>
            <Btn
              variant="primary"
              size="md"
              icon={RotateCcw}
              disabled={!recallDate || !recallDateValid || recall.loading}
              loading={recall.loading}
              loadingText="Recalling…"
              onClick={async () => {
                try {
                  await recall.mutate(undefined as never);
                  setRecallDate("");
                  setRecallReason("");
                  await reload();
                  toast.success("Leave recalled — unused days have been credited back.");
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
            >
              Confirm Recall
            </Btn>
          </div>
        </div>
      )}

      {/* ── Employee Early Return panel ── */}
      {isApproved && isOwnApplication && !canManage && (
        <div className="overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
          <div className="border-b border-blue-100 bg-blue-50 px-5 py-4">
            <div className="flex items-center gap-2">
              <ArrowLeftFromLine className="h-4 w-4 text-blue-600" />
              <h2 className="font-semibold text-blue-900">Report Early Return</h2>
            </div>
            <p className="mt-0.5 text-sm text-blue-700">
              Returned before your scheduled end date? Report it here and unused days will be credited back.
            </p>
          </div>
          <div className="space-y-4 p-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Date of Return <span className="text-red-500">*</span>
              </span>
              <input
                type="date"
                className="input"
                value={earlyReturnDate}
                min={data.startDate}
                max={data.endDate}
                onChange={(e) => setEarlyReturnDate(e.target.value)}
              />
              {earlyReturnDate && !earlyReturnDateValid && (
                <p className="text-xs font-semibold text-red-600">
                  Must be within the approved leave period ({formatDate(data.startDate)} – {formatDate(data.endDate)}).
                </p>
              )}
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">Reason <span className="text-slate-400 font-normal">(optional)</span></span>
              <textarea
                className="input min-h-[80px] resize-y"
                value={earlyReturnReason}
                onChange={(e) => setEarlyReturnReason(e.target.value)}
                placeholder="Optional — e.g. Personal reasons, emergency resolved…"
              />
            </label>
            <Btn
              variant="primary"
              size="md"
              icon={ArrowLeftFromLine}
              disabled={!earlyReturnDate || !earlyReturnDateValid || earlyReturn.loading}
              loading={earlyReturn.loading}
              loadingText="Submitting…"
              onClick={async () => {
                try {
                  await earlyReturn.mutate(undefined as never);
                  setEarlyReturnDate("");
                  setEarlyReturnReason("");
                  await reload();
                  toast.success("Early return recorded.");
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
            >
              Confirm Early Return
            </Btn>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{label}</p>
      <div className="mt-2">{value}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="mt-1 font-medium text-slate-800">{value}</div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * The backend stores only the username (e.g. "gaudenciah.jambiha" or "jdoe").
 * Convert dot/underscore-separated usernames to Title Case where possible.
 * Single tokens (e.g. "jdoe") are returned as-is.
 */
function formatUsername(raw?: string | null): string {
  if (!raw) return "";
  const parts = raw.split(/[._]/).filter(Boolean);
  if (parts.length < 2) return raw;
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return value; }
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  try { return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return value; }
}

function buildDetailRows(data: LeaveApplicationDetails): (string | number | null)[][] {
  const rows: (string | number | null)[][] = [
    ["Application No.", data.applicationNo],
    ["Employee No.",    data.employeeNumber],
    ["Employee Name",  data.employeeName],
    ["Department",     data.departmentName ?? ""],
    ["Job Title",      data.jobTitle ?? ""],
    ["Leave Type",     data.leaveTypeName],
    ["Start Date",     data.startDate],
    ["End Date",       data.endDate],
    ["Days Requested", data.requestedDays],
    ["Status",         data.status],
    ["Workflow",       data.workflowCode ?? ""],
    ["Current Step",   data.currentStepName ?? ""],
    ["Approver",       data.currentApproverUsername ?? ""],
    ["Relief Employee",data.reliefEmployeeName ?? ""],
    ["Reason",         data.reason ?? ""],
    ["Submitted At",   data.submittedAt ?? ""],
    ["Approved At",    data.approvedAt ?? ""],
    ["Rejected At",    data.rejectedAt ?? ""],
    ["Cancelled At",   data.cancelledAt ?? ""],
  ];
  if (data.status === "RECALLED") {
    rows.push(
      ["Actual Return Date", data.actualReturnDate ?? ""],
      ["Actual Days Used",   data.actualDaysUsed ?? ""],
      ["Recalled At",        data.recalledAt ?? ""],
      ["Recall Reason",      data.recallReason ?? ""],
    );
  }
  return rows;
}
